import { FormEvent, useEffect, useRef, useState } from "react";
import AutoSuggest from "react-autosuggest";
import { useDispatch } from "react-redux";

import "./StainInputGroup.css";

import { stainOrderRequest } from "shared/actions/caseDetailActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import selectLabStains, {
  MemoizedLabStain,
} from "shared/selectors/selectLabStains";
import selectSortedCaseDetail from "shared/selectors/selectSortedCaseDetail";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { useAppSelector } from "store/hooks";

import LabStainSuggestion from "./LabStainSuggestion";

interface StainInputGroupProps {
  slideDetail: SlideDetailState;
  stainCatalog: ReturnType<typeof selectLabStains>;
  setDialog?: Function;
}

type StainSuggestions = {
  title: string;
  stains: MemoizedLabStain[];
}[];

const sumTurnaroundTimes = (stains?: MemoizedLabStain[]) => {
  if (!stains) return 0;
  const totalTat = stains
    .map((s) => s.turnaround_hours)
    .filter((t) => t)
    // @ts-ignore (we filter t => t above)
    .reduce((a, b) => a + b, 0);
  return totalTat ?? 0;
};

const StainInputGroup = ({
  slideDetail,
  stainCatalog,
  setDialog,
}: StainInputGroupProps) => {
  const sortedCase = useAppSelector(selectSortedCaseDetail);
  const isLoading = useIsLoading(stainOrderRequest, {
    requestBody: { case_id: sortedCase?.id },
  });
  const unstnOrderQtyFeature = useAppSelector((state) =>
    selectFeatureFlag(state, "unstnOrderQty")
  );

  const [blockId, setBlockId] = useState<number | undefined>(
    slideDetail.block_obj?.id
  );
  const [labstain, setLabstain] = useState<MemoizedLabStain | undefined>(
    undefined
  );
  const [suggestions, setSuggestions] = useState<StainSuggestions>([]);
  const [stainPrefix, setStainPrefix] = useState("");
  const [note, setNote] = useState("");

  const dispatch = useDispatch();

  const currSlideId = useRef<number | undefined>(slideDetail.id);
  const inputRef = useRef<HTMLInputElement>(null);
  const quantityRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (currSlideId.current !== slideDetail.id) {
      setBlockId(slideDetail.block_obj?.id);
      currSlideId.current = slideDetail.id;
    }
  }, [slideDetail]);

  const filterStains = (prefix: string) =>
    stainCatalog
      ? Object.entries(
          stainCatalog
            .filter(
              (s) =>
                s.stain?.name.toUpperCase().includes(prefix.toUpperCase()) ||
                s.stain?.abbreviation.includes(prefix.toUpperCase())
            )
            .sort((a, b) =>
              (a.stain?.name || "") > (b.stain?.name || "") ? 1 : -1
            )
            .reduce((acc: { [key: string]: MemoizedLabStain[] }, value) => {
              const category = value.stain?.category || "unknown";
              if (acc[category]) {
                acc[category].push(value);
              } else {
                acc[category] = [value];
              }
              return acc;
            }, {})
        )
          .map(([title, stains]) => ({
            title,
            stains,
          }))
          .sort((a, b) => {
            return (
              sumTurnaroundTimes(a?.stains) - sumTurnaroundTimes(b?.stains)
            );
          })
      : [];

  const identifyStain = (prefix: string): MemoizedLabStain | undefined => {
    const filtered: MemoizedLabStain[] = filterStains(prefix)
      .reduce(
        (acc: MemoizedLabStain[], groupedStains) =>
          acc.concat(groupedStains.stains),
        []
      )
      .filter(
        (s) =>
          s.stain?.name.toUpperCase() === prefix.toUpperCase() ||
          s.stain?.abbreviation.toUpperCase() === prefix.toUpperCase()
      );

    return filtered && filtered.length > 0 ? filtered[0] : undefined;
  };

  const onSubmitStainOrder = (e: FormEvent) => {
    e.preventDefault();
    if (blockId && labstain) {
      const slide_count = Number(quantityRef.current?.value);
      dispatch(
        stainOrderRequest({
          requestBody: {
            case_id: sortedCase?.id || NaN,
            lab_stain_id: labstain.id,
            block_id: blockId,
            note: note ? note : undefined,
            slide_count:
              !Number.isNaN(slide_count) && slide_count && unstnOrderQtyFeature
                ? slide_count
                : 1,
          },
        })
      );
      setLabstain(undefined);
      setStainPrefix("");
      setNote("");
    }
  };

  return (
    <div className="StainInputGroup">
      <div className="newOrder__header">PLACE A NEW ORDER</div>
      <form className="newOrder__inputLine" onSubmit={onSubmitStainOrder}>
        <select
          id="newOrder__block"
          onChange={(e) => {
            setBlockId(Number(e.target.value));
          }}
          value={blockId ? `${blockId}` : ""}
        >
          <option key="default" value="">
            --
          </option>
          {sortedCase.blocks.map((block) => (
            <option key={`option_block_${block.id}`} value={block.id}>
              {block.full_name}
            </option>
          ))}
        </select>
        <div className="newOrder__inputLine__inputs">
          <AutoSuggest
            multiSection={true}
            suggestions={suggestions}
            getSuggestionValue={(s: MemoizedLabStain) =>
              s.stain?.abbreviation || ""
            }
            inputProps={{
              id: "NewStain__input",
              name: "NewStain__input",
              alt: "newStain__input",
              value: stainPrefix,
              ref: inputRef,
              placeholder: "Order Name",
              onKeyDown: (e) => {
                if (e.key === "Escape" && setDialog && !stainPrefix) {
                  setDialog(null);
                }
              },
              onChange: (_, { newValue }) => {
                setStainPrefix(newValue);
                setLabstain(identifyStain(newValue));
              },
              tabIndex: unstnOrderQtyFeature ? 1 : undefined,
            }}
            onSuggestionsFetchRequested={({ value }) => {
              const filtered = filterStains(value);
              setSuggestions(filtered);
            }}
            onSuggestionsClearRequested={() => {
              setSuggestions([]);
            }}
            renderSuggestion={(s: MemoizedLabStain, { query }) => (
              <LabStainSuggestion labstain={s} query={query} />
            )}
            onSuggestionSelected={(e, { suggestion, suggestionValue }) => {
              setStainPrefix(suggestionValue);
              setLabstain(suggestion);
            }}
            onSuggestionHighlighted={({ suggestion }) => {
              suggestion && setLabstain(suggestion);
            }}
            renderSectionTitle={(s) => s.title}
            getSectionSuggestions={(s) => s.stains}
            renderInputComponent={(props) => {
              return (
                <div className="newOrder__inputLineContainer">
                  <input {...props} />
                  {labstain?.stain?.category &&
                    labstain?.stain?.category !== "ROUTINE" && (
                      <span className="newOrder__inputLine__category">
                        {labstain?.stain?.category}
                      </span>
                    )}
                </div>
              );
            }}
          />
          <input
            type="text"
            placeholder="Additional Details"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            tabIndex={unstnOrderQtyFeature ? 4 : undefined}
          />
        </div>

        {unstnOrderQtyFeature && labstain?.stain.abbreviation === "UNSTN" && (
          <div>
            <input
              type="number"
              min={1}
              max={99}
              name="slide_count"
              placeholder="#"
              ref={quantityRef}
              tabIndex={unstnOrderQtyFeature ? 2 : undefined}
            />
          </div>
        )}

        <PathologyWatchButton
          title="Order"
          type="submit"
          disabled={!labstain || !blockId || isLoading}
          displaySpinner={isLoading}
          tabIndex={unstnOrderQtyFeature ? 3 : undefined}
        >
          {isLoading ? <>Ordering</> : <>Order</>}
        </PathologyWatchButton>
      </form>
    </div>
  );
};

export default StainInputGroup;
